import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, API_BASE, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Briefcase, AlertCircle, CheckCircle2, Building2, DollarSign, FileText,
  RefreshCw, Search, Warehouse, ShieldAlert, Download, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { PortalPriorities, PortalCapitalPlan, PortalAskAI } from "@/components/capital/PortalCapital";
import { PortalMethodology } from "@/components/capital/PortalMethodology";
import { AssetDecisionDialog } from "@/components/capital/AssetDecisionDialog";
import { ClientScope3Panel } from "@/components/sustainability/ClientScope3Panel";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import PortalDocumentsTab from "@/components/portal/PortalDocumentsTab";
import InvestmentProfile from "@/components/capital/InvestmentProfile";
import PortalRiskProfile from "@/components/portal/PortalRiskProfile";

function KpiCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const iconTone = tone === "warn"   ? "bg-amber-100 text-amber-700"
                : tone === "good"    ? "bg-emerald-100 text-emerald-700"
                : tone === "danger"  ? "bg-rose-100 text-rose-700"
                :                      "bg-[#003a5c]/10 text-[#003a5c]";
  const border = tone === "warn"    ? "border-amber-200/80"
              : tone === "good"     ? "border-emerald-200/80"
              : tone === "danger"   ? "border-rose-200/80"
              :                       "border-slate-200/80";
  return (
    <div data-testid={testid}
         className={`rounded-xl border ${border} bg-white p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}>
      <div className="flex items-center gap-2.5">
        {Icon && (
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconTone}`}>
            <Icon className="w-4 h-4" />
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-2.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

export default function ClientPortalHomePage() {
  const { role: permRole } = usePermissions();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [assets, setAssets] = useState([]);
  const [reports, setReports] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [jobQ, setJobQ] = useState("");
  const [horizonFilter, setHorizonFilter] = useState("__all");
  const [decisionAsset, setDecisionAsset] = useState(null);

  const role = (permRole || "").toLowerCase();
  const isAdmin = role === "executive" || role === "admin" || role === "finance_admin";
  const clientOverride = params.get("client_id");

  // Executive/admin can pick a client to preview
  const [clients, setClients] = useState([]);
  useEffect(() => {
    if (isAdmin) {
      api.get("/master/clients")
        .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.items || [])))
        .catch(() => {});
    }
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const qp = clientOverride ? `?client_id=${clientOverride}` : "";
      const [s, j, i, a, rp] = await Promise.all([
        api.get(`/client-portal/summary${qp}`),
        api.get(`/client-portal/jobs${qp}${qp ? "&" : "?"}limit=100`),
        api.get(`/client-portal/invoices${qp}${qp ? "&" : "?"}limit=100`),
        api.get(`/client-portal/assets${qp}${qp ? "&" : "?"}limit=200`),
        api.get(`/client-portal/reports${qp}`),
      ]);
      setSummary(s.data);
      setJobs(j.data.items || []);
      setInvoices(i.data);
      setAssets(a.data.items || []);
      setReports(rp.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Client Portal");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientOverride]);

  const filteredJobs = useMemo(() => {
    if (!jobQ) return jobs;
    const s = jobQ.toLowerCase();
    return jobs.filter((j) =>
      `${j.title} ${j.job_number} ${j.site_name}`.toLowerCase().includes(s)
    );
  }, [jobs, jobQ]);

  const filteredAssets = useMemo(() => {
    if (horizonFilter === "__all") return assets;
    return assets.filter((a) => a.horizon === horizonFilter);
  }, [assets, horizonFilter]);

  const scope = summary?.scope || {};

  const changeClient = (id) => {
    if (id) params.set("client_id", id); else params.delete("client_id");
    setParams(params);
  };

  return (
    <AppLayout
      title="Client Portal"
      subtitle={scope.client_name
        ? `Live view for ${scope.client_name}${isAdmin ? " (admin preview)" : ""}`
        : "Read-only client-facing workspace"}
      actions={
        <div className="flex items-center gap-2">
          {isAdmin && clients.length > 0 && (
            <SearchableSelect
              value={scope.client_id || ""}
              onChange={changeClient}
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Preview a client…"
              width="w-[280px]"
              testid="portal-client-select"
            />
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="portal-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {isAdmin && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
          <ShieldAlert className="w-3.5 h-3.5" />
          <span>
            <strong>Admin preview mode</strong> — you are seeing this client's read-only portal exactly as they would.
          </span>
        </div>
      )}

      {/* Welcome hero */}
      <div data-testid="portal-hero"
           className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#003a5c] via-[#00304d] to-[#001e2c] text-white px-6 sm:px-8 py-6 mb-5 shadow-xl">
        <div className="absolute -right-16 -top-20 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -right-4 bottom-[-70px] w-48 h-48 rounded-full bg-[#fcea00]/10" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#fcea00] font-semibold">GBE Group · Client Portal</div>
          <div className="font-display text-2xl sm:text-3xl font-bold mt-1.5 tracking-tight">
            Welcome{scope.client_name ? `, ${scope.client_name}` : ""}
          </div>
          <div className="text-sm text-white/75 mt-1.5 max-w-2xl leading-relaxed">
            Your live window into the work GBE Group is doing across your portfolio — jobs, asset condition,
            capital planning, invoices and reports, always up to date.
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {summary?.sites_count ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px]">
                <Building2 className="w-3 h-3" /> {summary.sites_count} site{summary.sites_count === 1 ? "" : "s"} managed
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px]">
              <Warehouse className="w-3 h-3" /> {fmtNumber(summary?.assets_count ?? 0)} assets tracked
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px]">
              <CheckCircle2 className="w-3 h-3" /> {summary?.completed_jobs_30d ?? 0} jobs completed in 30 days
            </span>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Open jobs"          value={fmtNumber(summary?.open_jobs ?? 0)}
                 sub={`${summary?.overdue_jobs ?? 0} overdue`}
                 icon={Briefcase} testid="portal-kpi-open"
                 tone={(summary?.overdue_jobs ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard label="Completed (30d)"    value={fmtNumber(summary?.completed_jobs_30d ?? 0)}
                 sub="Last 30 days" icon={CheckCircle2} testid="portal-kpi-completed" tone="good" />
        <KpiCard label="Assets"             value={fmtNumber(summary?.assets_count ?? 0)}
                 sub={`${summary?.high_risk_assets ?? 0} high-risk`}
                 icon={Warehouse} testid="portal-kpi-assets"
                 tone={(summary?.high_risk_assets ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard label="Invoices outstanding" value={fmtCurrency(summary?.invoices_outstanding_amount ?? 0)}
                 sub={`${summary?.invoices_outstanding_count ?? 0} open`}
                 icon={DollarSign} testid="portal-kpi-invoices"
                 tone={(summary?.invoices_outstanding_amount ?? 0) > 0 ? "warn" : "default"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100/80 border border-slate-200/80 p-1.5 h-auto flex-wrap gap-1 justify-start rounded-xl shadow-inner">
          <TabsTrigger data-testid="portal-tab-overview" value="overview" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Overview</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-priorities" value="priorities" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Priorities</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-capital" value="capital" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Capital Plan</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-methodology" value="methodology" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Methodology</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-risk-profile" value="risk-profile" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Risk Profile</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-assets"   value="assets"   className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Sites &amp; Assets</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-jobs"     value="jobs"     className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Jobs</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-invoices" value="invoices" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Invoices</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-documents" value="documents" className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Documents</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-sustainability" value="sustainability" className="text-xs rounded-lg transition-all data-[state=active]:bg-emerald-700 data-[state=active]:text-white data-[state=active]:shadow-sm">Sustainability</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-reports"  value="reports"  className="text-xs rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-semibold">Reports</TabsTrigger>
          <TabsTrigger data-testid="portal-tab-askai"    value="askai"    className="text-xs rounded-lg transition-all data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">Ask AI Agent</TabsTrigger>
        </TabsList>

        {/* Priorities — decision-focused (brief §13/§17) */}
        <TabsContent value="priorities" className="mt-4">
          <PortalPriorities assets={assets} onRespond={setDecisionAsset} />
        </TabsContent>

        {/* Capital Plan (brief §13) */}
        <TabsContent value="capital" className="mt-4">
          <PortalCapitalPlan clientName={scope.client_name} />
          <InvestmentProfile client={scope.client_name} />
        </TabsContent>

        {/* Ask AI Agent (brief §13/§14) */}
        <TabsContent value="methodology" className="mt-4">
          <PortalMethodology qp={clientOverride ? `?client_id=${clientOverride}` : ""} onApplied={load} />
        </TabsContent>

        <TabsContent value="risk-profile" className="mt-4">
          <PortalRiskProfile clientId={clientOverride || null} />
        </TabsContent>

        <TabsContent value="askai" className="mt-4">
          <PortalAskAI clientName={scope.client_name} />
        </TabsContent>

        {/* Sustainability — client Scope 3 attribution */}
        <TabsContent value="sustainability" className="mt-4">
          <ClientScope3Panel clientName={isAdmin ? scope.client_name : undefined} />
        </TabsContent>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="portal-welcome">
              <div className="text-[10px] uppercase tracking-wider text-[#003a5c] font-semibold">Your portal</div>
              <div className="text-lg font-display font-semibold text-slate-900 mt-1">
                {scope.client_name || "Your organisation"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {summary?.sites_count ? `${summary.sites_count} site${summary.sites_count === 1 ? "" : "s"} managed by GBE Group` : ""}
              </div>
              <div className="mt-4 space-y-2.5 text-sm text-slate-700 leading-relaxed">
                <p>
                  This portal gives you a live, read-only view of the work GBE Group is doing across your portfolio —
                  active jobs, asset condition, invoices and board reports.
                </p>
                <p>
                  Use the tabs above to drill in. For any questions or requests, contact your GBE Group account manager —
                  we're always happy to help.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="portal-quick-stats">
              <div className="text-[10px] uppercase tracking-wider text-[#003a5c] font-semibold mb-3">Quick stats</div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-600">Sites managed</span>
                  <span className="font-medium text-slate-900">{summary?.sites_count ?? 0}</span>
                </li>
                <li className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-600">Assets in the portfolio</span>
                  <span className="font-medium text-slate-900">{fmtNumber(summary?.assets_count ?? 0)}</span>
                </li>
                <li className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-600">Jobs completed (30d)</span>
                  <span className="font-medium text-emerald-700">{summary?.completed_jobs_30d ?? 0}</span>
                </li>
                <li className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-600">Board reports available</span>
                  <span className="font-medium text-slate-900">{summary?.board_reports_available ?? 0}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-slate-600">Portal tier</span>
                  <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">
                    {scope.portal_tier || "share_link"}
                  </Badge>
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* Jobs */}
        <TabsContent value="jobs" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="portal-jobs-search" value={jobQ} onChange={(e) => setJobQ(e.target.value)}
                placeholder="Search your jobs…" className="pl-9 h-9 bg-white" />
            </div>
            <div className="ml-auto text-xs text-slate-500">{filteredJobs.length} of {jobs.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {filteredJobs.length === 0 ? (
              <div className="p-10 text-center" data-testid="portal-jobs-empty">
                <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-sm font-medium text-slate-700">No jobs to show yet</div>
                <div className="text-xs text-slate-500 mt-1">Work completed across your sites will appear here as it lands.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-medium px-3 py-2">Job #</th>
                      <th className="text-left font-medium px-3 py-2">Title</th>
                      <th className="text-left font-medium px-3 py-2">Site</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                      <th className="text-left font-medium px-3 py-2">Type</th>
                      <th className="text-left font-medium px-3 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.slice(0, 200).map((j) => (
                      <tr key={j.id} data-testid={`portal-job-${j.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{j.job_number || "—"}</td>
                        <td className="px-3 py-2 text-slate-800 max-w-md truncate">{j.title || "—"}</td>
                        <td className="px-3 py-2 text-slate-700 max-w-xs truncate">{j.site_name || "—"}</td>
                        <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{j.status}</Badge></td>
                        <td className="px-3 py-2 text-xs text-slate-600">{j.task_type || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(j.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Assets */}
        <TabsContent value="assets" className="mt-4 space-y-3">
          <SiteQRBlock clientId={scope.client_id} />
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" className="h-9 text-xs order-last" data-testid="portal-export-register"
                    onClick={async () => {
                      try {
                        const r = await api.get(`/client-portal/register-export${clientOverride ? `?client_id=${clientOverride}` : ""}`, { responseType: "blob" });
                        const u = URL.createObjectURL(r.data);
                        const el = document.createElement("a"); el.href = u; el.download = "asset_register.csv"; el.click();
                        URL.revokeObjectURL(u);
                      } catch { /* noop */ }
                    }}>
              Download asset register (CSV)
            </Button>
            <Select value={horizonFilter} onValueChange={setHorizonFilter}>
              <SelectTrigger className="w-[240px] h-9 bg-white" data-testid="portal-assets-filter">
                <SelectValue placeholder="Renewal horizon" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All horizons</SelectItem>
                <SelectItem value="Immediate Attention">Beyond Design Life</SelectItem>
                <SelectItem value="12 Month Plan">12 Month Plan</SelectItem>
                <SelectItem value="24 Month Plan">24 Month Plan</SelectItem>
                <SelectItem value="Deferred">Deferred</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-slate-500">{filteredAssets.length} of {assets.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {filteredAssets.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No assets in this filter.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-medium px-3 py-2">Asset</th>
                      <th className="text-left font-medium px-3 py-2">Class</th>
                      <th className="text-left font-medium px-3 py-2">Site</th>
                      <th className="text-right font-medium px-3 py-2">Condition</th>
                      <th className="text-right font-medium px-3 py-2">Priority</th>
                      <th className="text-left font-medium px-3 py-2">Horizon</th>
                      <th className="text-right font-medium px-3 py-2">Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.slice(0, 250).map((a) => (
                      <tr key={a.id} data-testid={`portal-asset-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 text-slate-800 max-w-md truncate">{a.asset_name || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{a.asset_group || "—"}</td>
                        <td className="px-3 py-2 text-slate-700 max-w-xs truncate">{a.site_name || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{a.condition ?? "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{Math.round(a.priority_score ?? 0)}</td>
                        <td className="px-3 py-2">
                          <Badge className={`text-[10px] ${
                            a.horizon === "Immediate Attention" ? "bg-rose-100 text-rose-700 hover:bg-rose-100" :
                            a.horizon === "12 Month Plan"       ? "bg-amber-100 text-amber-700 hover:bg-amber-100" :
                            a.horizon === "24 Month Plan"       ? "bg-blue-100 text-blue-700 hover:bg-blue-100" :
                                                                    "bg-slate-100 text-slate-700 hover:bg-slate-100"
                          }`}>{a.horizon === "Immediate Attention" ? "Beyond Design Life" : (a.horizon || "—")}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(a.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Documents — read-only client library */}
        <TabsContent value="documents" className="mt-4">
          <PortalDocumentsTab clientId={scope.client_id} />
        </TabsContent>

        {/* Invoices */}
        <TabsContent value="invoices" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Invoices</span>
              {invoices && (
                <span className="ml-auto text-xs text-slate-600">
                  Outstanding <span className="font-semibold text-slate-900">{fmtCurrency(invoices.total_outstanding)}</span>
                </span>
              )}
            </div>
            {(!invoices?.items || invoices.items.length === 0) ? (
              <div className="p-6 text-sm text-slate-500 text-center">No invoices on record yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-medium px-3 py-2">Invoice #</th>
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Due</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                      <th className="text-right font-medium px-3 py-2">Outstanding</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.items.map((inv) => (
                      <tr key={inv.id} data-testid={`portal-invoice-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-700">{inv.invoice_number || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{(inv.date || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{(inv.due_date || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(inv.amount)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(inv.amount_due)}</td>
                        <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{inv.status || "—"}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Board Reports */}
        <TabsContent value="reports" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Board Reports shared with you</span>
            </div>
            {reports.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">
                No board reports have been shared yet. Your GBE account manager will publish them here.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {reports.map((r) => (
                  <li key={r.token} data-testid={`portal-report-${r.token}`}
                      className="px-4 py-3 flex flex-wrap items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{r.label || "Strategic Renewal Assessment"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Expires {(r.expires_at || "").slice(0, 10)} · {r.view_count} views
                      </div>
                    </div>
                    <Button size="sm" variant="outline" asChild data-testid={`portal-report-open-${r.token}`}>
                      <a href={`/client-view/${r.token}`} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
                      </a>
                    </Button>
                    <Button size="sm" variant="outline" asChild data-testid={`portal-report-pdf-${r.token}`}>
                      <a href={`${API_BASE}/capital/public/board-report/${r.token}.pdf`} target="_blank" rel="noreferrer">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <AssetDecisionDialog asset={decisionAsset} onOpenChange={(o) => !o && setDecisionAsset(null)}
        onSaved={(u) => setAssets((prev) => prev.map((a) =>
          a.asset_id === u.asset_id ? { ...a, client_decision: u.client_decision, decision_status: u.decision_status } : a))} />
    </AppLayout>
  );
}

function SiteQRBlock({ clientId }) {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/site-qr/sites?client_id=${clientId}`)
      .then((r) => setSites(r.data.sites || []))
      .catch(() => {});
  }, [clientId]);
  const download = async (kind) => {
    if (!siteId) { toast.error("Choose a site first"); return; }
    setBusy(true);
    try {
      const path = kind === "pdf" ? `/site-qr/${siteId}/poster.pdf` : `/site-qr/${siteId}/qr.png`;
      const r = await api.get(path, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = u;
      el.download = kind === "pdf" ? "GBE-Site-QR-Poster.pdf" : "site-qr.png";
      el.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
    setBusy(false);
  };
  if (sites.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid="portal-site-qr-block">
      <div className="text-xs font-medium text-slate-600">Site QR posters</div>
      <Select value={siteId} onValueChange={setSiteId}>
        <SelectTrigger className="w-[280px] h-8 text-xs bg-white" data-testid="portal-site-qr-select">
          <SelectValue placeholder="Choose a site…" />
        </SelectTrigger>
        <SelectContent>
          {sites.map((s) => (
            <SelectItem key={s.id} value={s.id}>{s.name} ({s.asset_count} assets)</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
              data-testid="portal-site-qr-poster" onClick={() => download("pdf")}>
        Download A4 poster (PDF)
      </Button>
      <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy}
              data-testid="portal-site-qr-png" onClick={() => download("png")}>
        QR image only
      </Button>
      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
              data-testid="portal-site-qr-batch"
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.get(`/site-qr/posters.pdf?client_id=${clientId}`, { responseType: "blob", timeout: 120000 });
                  const u = URL.createObjectURL(r.data);
                  const el = document.createElement("a");
                  el.href = u; el.download = "GBE-Site-QR-Posters-All-Sites.pdf"; el.click();
                  URL.revokeObjectURL(u);
                } catch { toast.error("Batch download failed"); }
                setBusy(false);
              }}>
        All sites ({sites.length}) — one PDF
      </Button>
      <span className="text-[10px] text-slate-400">Scan onsite → login → live site asset view</span>
    </div>
  );
}
